import { SkyDataGrid, SkyInput, SkyText, type SkyDataGridColumn } from "@skypointcloud/skypoint-ui";
import { useMemo, useState } from "react";
import { LIST_GRID_PAGINATION } from "@/lib/grid";

/**
 * The table picker of table-level runs (product gaps row 2): a searchable grid with a checkbox
 * per table, shared by Run now and "Retry N tables". Holds no selection of its own: the host owns
 * the chosen names, so a search that hides a ticked table never unticks it.
 *
 * With `onSearchChange` the host searches (Run now asks the server, since a connection can hold
 * far more tables than one page lists), and the rows it passes are shown as given.
 */

export interface TablePickerRow {
  name: string;
  /** Extra cells, keyed by the extra columns' keys. */
  [key: string]: string;
}

export interface TablePickerProps {
  rows: readonly TablePickerRow[];
  selected: readonly string[];
  onChange: (names: string[]) => void;
  /** Columns after the table name, e.g. "What happened". */
  extraColumns?: Array<{ key: string; header: string }>;
  /** The grid's accessible name. */
  ariaLabel: string;
  loading?: boolean;
  /** Said under the grid when the list is cut, e.g. "The first 200 tables are listed." */
  note?: string | null;
  /** Hands the typed search to the host, which passes rows already searched. */
  onSearchChange?: (search: string) => void;
  /** Said when the rows could not be read. */
  error?: string | null;
  /**
   * The host's search is settling or being answered: it passes only the rows it already holds,
   * and "Loading tables." is said until the answer is in.
   */
  searchPending?: boolean;
  testId: string;
}

export function TablePicker({
  rows,
  selected,
  onChange,
  extraColumns = [],
  ariaLabel,
  loading = false,
  note = null,
  onSearchChange,
  error = null,
  searchPending = false,
  testId,
}: TablePickerProps) {
  const [search, setSearch] = useState("");
  const visible = useMemo(() => {
    const needle = onSearchChange ? "" : search.trim().toLowerCase();
    return needle ? rows.filter((row) => row.name.toLowerCase().includes(needle)) : [...rows];
  }, [rows, search, onSearchChange]);
  const columns: SkyDataGridColumn<TablePickerRow>[] = [
    { key: "name", header: "Table", width: extraColumns.length > 0 ? "40%" : "100%" },
    ...extraColumns.map((column) => ({
      key: column.key,
      header: column.header,
      width: `${Math.floor(60 / extraColumns.length)}%`,
    })),
  ];
  const visibleNames = new Set(visible.map((row) => row.name));

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <SkyInput
        label="Search tables"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          onSearchChange?.(event.target.value);
        }}
        testId={`${testId}-search`}
      />
      <SkyDataGrid<TablePickerRow>
        data={visible}
        columns={columns}
        getRowId={(row) => row.name}
        selectionMode="multiple"
        selectedRows={selected.filter((name) => visibleNames.has(name))}
        onSelectionChange={(ids) => {
          // Reconcile against the rows on screen only: a ticked table the search hides stays.
          const shown = new Set(ids.map(String));
          const kept = selected.filter((name) => !visibleNames.has(name));
          onChange([...kept, ...visible.filter((row) => shown.has(row.name)).map((r) => r.name)]);
        }}
        {...LIST_GRID_PAGINATION}
        loading={(loading || searchPending) && rows.length === 0}
        ariaLabel={ariaLabel}
        // With the list unread, "no tables" would be false: the error line below says why.
        emptyMessage={
          error ? "" : search ? "No table matches that search." : "No tables to choose from."
        }
        testId={`${testId}-grid`}
      />
      {searchPending ? (
        <SkyText size={200} color="muted" block role="status" testId={`${testId}-loading`}>
          Loading tables.
        </SkyText>
      ) : null}
      {error ? (
        <SkyText size={200} color="danger" block role="alert" testId={`${testId}-error`}>
          {error}
        </SkyText>
      ) : null}
      <SkyText size={200} color="muted" block testId={`${testId}-count`}>
        {selected.length === 1 ? "1 table chosen." : `${selected.length} tables chosen.`}
        {note ? ` ${note}` : ""}
      </SkyText>
    </div>
  );
}
